import { Component, type ErrorInfo, type ReactNode } from "react";
import { modules, type SlotId, type SlotProps } from "../../platform/registry/registry";

/**
 * A named extension point.
 *
 * Core renders <Slot id="till.item.badge" /> without knowing, or caring, what
 * appears there. With no module enabled it renders nothing at all — not an empty
 * box, not a placeholder — which is what makes "modules off leaves no orphaned
 * interface elements" (SC-012) true structurally rather than by tidying up.
 */

interface SlotBoundaryProps {
  moduleId: string;
  children: ReactNode;
}

interface SlotBoundaryState {
  failed: boolean;
}

/**
 * A module whose slot component throws must not take the till down with it.
 * The same reasoning as subscriber isolation in the event bus: a broken module
 * may degrade its own feature and nothing else.
 */
class SlotBoundary extends Component<SlotBoundaryProps, SlotBoundaryState> {
  override state: SlotBoundaryState = { failed: false };

  static getDerivedStateFromError(): SlotBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[slot] module "${this.props.moduleId}" failed to render`, error, info);
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

interface SlotComponentProps extends SlotProps {
  id: SlotId;
}

export function Slot({ id, ...props }: SlotComponentProps) {
  const entries = modules.slotComponents(id);
  if (entries.length === 0) return null;

  return (
    <>
      {entries.map(({ moduleId, component: Rendered }) => (
        <SlotBoundary key={moduleId} moduleId={moduleId}>
          <Rendered {...props} />
        </SlotBoundary>
      ))}
    </>
  );
}

export default Slot;
